<i18n>
[
    "a11y__accordion--expand",
    "a11y__accordion--collapse",
]
</i18n>

<template>
    <div class="c-accordion" :class="{ 'c-accordion--open': isOpen }">
        <div
            v-tab-focus.onspace="() => toggleAccordion()"
            class="c-accordion__header"
            :aria-label="labelText"
            :aria-expanded="isOpen"
            :aria-controls="safeId"
            :class="{
                [headerClass]: !!headerClass,
            }"
        >
            <slot name="header"></slot>
            <div class="c-accordion__header-icon">
                <BaseIcon
                    :icon="isOpen ? 'global--minus' : 'global--plus'"
                    size="16px"
                    color="primary"
                />
            </div>
        </div>
        <div v-show="isOpen" :id="safeId" class="c-accordion__body">
            <slot name="body"></slot>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { uniqueId } from 'lodash-es';

import { useActions } from '~coreModules/core/js/composables/vuexHelpers';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { ACCORDION_OPENED } from '~coreModules/core/js/global-event-constants';

const props = defineProps({
    headerClass: {
        type: String,
        default: '',
    },
    isInitiallyOpen: {
        type: Boolean,
        default: false,
    },
    isTopBorderless: {
        type: Boolean,
        default: false,
    },
    moduleId: {
        type: String,
        default: '',
    },
    analyticsTitle: {
        type: String,
        default: '',
    },
    title: {
        type: String,
        default: '',
    },
});
const emit = defineEmits(['toggle']);

const { t } = useI18n();

const isOpen = ref(props.isInitiallyOpen);

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

const labelText = computed(() => (isOpen.value ? t('a11y__accordion--collapse', [props.title]) :
    t('a11y__accordion--expand', [props.title])));

const safeId = computed(() => (props.title ? props.title.replace(/\s+/g, '-').toLowerCase() : uniqueId('accordion-')));

function toggleAccordion() {
    isOpen.value = !isOpen.value;

    if (isOpen.value) {
        trackGlobalEvent({
            type: ACCORDION_OPENED,
            data: {
                accordionTitle: props.analyticsTitle,
            },
        });
    }
    emit('toggle', isOpen.value);
}
</script>

<style lang="scss">
    .c-accordion {
        border-bottom: 1px solid $nu-white;

        &__header {
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: $nu-spacer-1pt5 0;
        }

        &__header-icon {
            display: flex;
            margin: 0 $nu-spacer-2 0 $nu-spacer-3;
            height: 100%;
        }

        &__body {
            padding-bottom: $nu-spacer-1pt5;
        }
    }
</style>
